export interface Vec3Like {
  x: number;
  y: number;
  z: number;
}

export class Vec3 {
  static readonly ZERO = new Vec3(0, 0, 0);
  static readonly UP = new Vec3(0, 1, 0);
  static readonly FORWARD = new Vec3(0, 0, -1);

  static from(value: Vec3Like): Vec3 {
    return value instanceof Vec3 ? value : new Vec3(value.x, value.y, value.z);
  }

  constructor(
    readonly x: number,
    readonly y: number,
    readonly z: number,
  ) {}

  add(other: Vec3Like): Vec3 {
    return new Vec3(this.x + other.x, this.y + other.y, this.z + other.z);
  }

  sub(other: Vec3Like): Vec3 {
    return new Vec3(this.x - other.x, this.y - other.y, this.z - other.z);
  }

  scale(scalar: number): Vec3 {
    return new Vec3(this.x * scalar, this.y * scalar, this.z * scalar);
  }

  dot(other: Vec3Like): number {
    return this.x * other.x + this.y * other.y + this.z * other.z;
  }

  cross(other: Vec3Like): Vec3 {
    return new Vec3(
      this.y * other.z - this.z * other.y,
      this.z * other.x - this.x * other.z,
      this.x * other.y - this.y * other.x,
    );
  }

  length(): number {
    return Math.hypot(this.x, this.y, this.z);
  }

  normalize(fallback: Vec3Like = Vec3.ZERO): Vec3 {
    const len = this.length();
    if (len <= 1e-6) return Vec3.from(fallback);
    return new Vec3(this.x / len, this.y / len, this.z / len);
  }

  distanceTo(other: Vec3Like): number {
    return this.sub(other).length();
  }

  midpoint(other: Vec3Like): Vec3 {
    return new Vec3((this.x + other.x) / 2, (this.y + other.y) / 2, (this.z + other.z) / 2);
  }

  toObject(): Vec3Like {
    return { x: this.x, y: this.y, z: this.z };
  }

  toJSON(): Vec3Like {
    return this.toObject();
  }
}
